import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { OverlayPanel } from 'primereact/overlaypanel';

interface VisibilityControlToggleProps {
  visibleEntities: {
    all: boolean;
    plannedShifts: boolean;
    manualShifts: boolean;
    departmentSchedule: boolean;
    absences: boolean;
    closedTask: boolean;
    notClosedTask: boolean
  };
  onApply: (entities: {
    all: boolean;
    plannedShifts: boolean;
    manualShifts: boolean;
    departmentSchedule: boolean;
    absences: boolean;
    closedTask: boolean;
    notClosedTask: boolean
  }) => void;
}

const VisibilityControlToggle: React.FC<VisibilityControlToggleProps> = ({
  visibleEntities,
  onApply
}) => {
  const [selectedEntities, setSelectedEntities] = useState(visibleEntities);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isApplyClicked, setIsApplyClicked] = useState(false);
  const op = useRef<OverlayPanel>(null);

  const ENTITY_KEYS: (keyof typeof selectedEntities)[] = [
    'plannedShifts',
    'manualShifts',
    'departmentSchedule',
    'absences',
    'closedTask',
    'notClosedTask'
  ];

  useEffect(() => {
    if (!isOverlayVisible && !isApplyClicked) {
      setSelectedEntities(visibleEntities);
    }
  }, [isOverlayVisible, isApplyClicked, visibleEntities]);

  const handleApply = (e: React.SyntheticEvent) => {
    onApply(selectedEntities);
    setIsApplyClicked(true);
    op.current?.hide();
  };

  const handleCheckboxChange = (entity: keyof typeof selectedEntities) => {
    setSelectedEntities((prev) => {
      const newValue = !prev[entity];

      if (entity === 'all') {
        // Массовое выделение
        return {
          all: newValue,
          plannedShifts: newValue,
          manualShifts: newValue,
          departmentSchedule: newValue,
          absences: newValue,
          tasks: newValue,
          closedTask: newValue,
          notClosedTask: newValue
        };
      }

      // Изменяем выбранный чекбокс
      const newState = { ...prev, [entity]: newValue };
      // Всегда пересчитываем all: если все дочерние true — ставим all:true, иначе false
      const allChecked = ENTITY_KEYS.every((key) => newState[key]);
      return { ...newState, all: allChecked };
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <Button
        icon="pi pi-eye"
        tooltip="Управление видимостью элементов календаря"
        tooltipOptions={{ position: 'top' }}
        onClick={(e) => {
          setIsApplyClicked(false);
          op.current?.toggle(e);
        }}
        className="p-button-rounded"
        severity={
          ENTITY_KEYS.some((key) => !visibleEntities[key])
            ? 'warning'
            : 'secondary'
        }
      />
      <OverlayPanel
        ref={op}
        showCloseIcon
        dismissable
        onShow={() => setIsOverlayVisible(true)}
        onHide={() => setIsOverlayVisible(false)}
      >
        <div className="flex flex-column gap-3">
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="selectAll"
              checked={selectedEntities.all}
              onChange={() => handleCheckboxChange('all')}
            />
            <label htmlFor="selectAll" className="ml-2 font-normal">
              Выбрать все
            </label>
          </div>
          <Divider className="m-0" />

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="plannedShifts"
              checked={selectedEntities.plannedShifts}
              onChange={() => handleCheckboxChange('plannedShifts')}
            />
            <label htmlFor="plannedShifts" className="ml-2 font-normal">
              Плановые смены
            </label>
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="manualShifts"
              checked={selectedEntities.manualShifts}
              onChange={() => handleCheckboxChange('manualShifts')}
            />
            <label htmlFor="manualShifts" className="ml-2 font-normal">
              Измененные смены
            </label>
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="departmentSchedule"
              checked={selectedEntities.departmentSchedule}
              onChange={() => handleCheckboxChange('departmentSchedule')}
            />
            <label htmlFor="departmentSchedule" className="ml-2 font-normal">
              График участка
            </label>
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="absences"
              checked={selectedEntities.absences}
              onChange={() => handleCheckboxChange('absences')}
            />
            <label htmlFor="absences" className="ml-2 font-normal">
              Отсутствия
            </label>
          </div>

          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="notClosedTask"
              checked={selectedEntities.notClosedTask}
              onChange={() => handleCheckboxChange('notClosedTask')}
            />
            <label htmlFor="notClosedTask" className="ml-2 font-normal">
              Открытые наряды
            </label>
          </div>
          
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="closedTask"
              checked={selectedEntities.closedTask}
              onChange={() => handleCheckboxChange('closedTask')}
            />
            <label htmlFor="closedTask" className="ml-2 font-normal">
              Закрытые наряды
            </label>
          </div>

          <Button
            label="Применить"
            onClick={handleApply}
            className="p-button-sm align-self-end"
            icon="pi pi-check"
          />
        </div>
      </OverlayPanel>
    </div>
  );
};

export default VisibilityControlToggle;
